import { Cancel, CheckCircle, Close, Download, Warning } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  TableContainer,
  Typography,
} from "@mui/material";
import { Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import { useEffect, useState } from "react";
import {
  ApiDetailsModal,
  getComplexityBGC,
  getComplexityColor,
  getComplexityLabel,
} from "../../specific/MigrationTool/Assessment/ApiDetailsModal";
import { AIViewIcon } from "../../specific/Icons/ViewComments/ViewComments";
import { SourceUploadComment, SourceUploadCommentDetail } from "../../../types/migrations";

interface DataTableProps {
  columns: string[];
  data: any[];
  download?: any;
  migrationId?: string;
}

const CustomTable: React.FC<DataTableProps> = ({ columns, data, download, migrationId }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [dataTable, setDataTable] = useState<any>([]);

  // Modal detail
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApi, setSelectedApi] = useState("");

  const [selectedComments, setSelectedComments] = useState<string[]>([]);
  const [selectedApiName, setSelectedApiName] = useState<string>("");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [selectedSourceUploadComments, setSelectedSourceUploadComments] = useState<SourceUploadComment[] | null>(null);
  const [errorsOpen, setErrorsOpen] = useState(false);

  const headers: any = [
    { key: "apiName", label: "API Name" },
    { key: "apiSourceFileName", label: "Original File" },
    { key: "apiDestinationFileName", label: "Migrated File" },
    { key: "status", label: "Status" },
    { key: "comments", label: "LLM Comments" },
    { key: "sourceUploadComments", label: "Target System Comments" },
  ];

  const headersTable = columns.length === 0 ? headers : columns;

  useEffect(() => {
    setDataTable(data);
  }, [data]);

  const handleSort = (key: any) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedData = [...data].sort((a, b) => {
      const valA = a[key] ?? "";
      const valB = b[key] ?? "";
      const compare = valA.toString().localeCompare(valB.toString());
      return direction === "asc" ? compare : -compare;
    });
    setDataTable(sortedData);
    setSortConfig({ key, direction });
  };

  //Function to handle download file migrated
  const handleDownloaMigratedFIle = (file: any) => {
    console.log(`Summary - handleDownloadSourceFile for file : ${file}`);
    download("TARGET", file);
  };

  // Function to handle download of source files
  const handleDownloadOriginalFIle = (file: any) => {
    console.log(`Summary - handleDownloadSourceFile for file : ${file}`);
    download("SOURCE", file);
  };

  // Open detail api assessment
  const handleDetailClick = (apiInfo: any, param2?: any, param3?: any) => {
    setSelectedApi(apiInfo);
    setIsModalOpen(true);
  };

  // Function to open comments drawer
  const handleViewComments = (file: any) => {
    setSelectedComments(file.comments || []);
    setSelectedApiName(file.apiName || "API");
    setCommentsOpen(true);
  };

  // Function to open errors drawer
  const handleViewErrors = (file: any) => {
    setSelectedSourceUploadComments(file.sourceUploadComments || null);
    setSelectedApiName(file.apiName || "API");
    setErrorsOpen(true);
  };

  // Functions to close drawers
  const handleCloseComments = () => {
    setCommentsOpen(false);
    setSelectedComments([]);
  };

  const handleCloseErrors = () => {
    setErrorsOpen(false);
    setSelectedSourceUploadComments(null);
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {headersTable.map((column: any) => (
                // <TableCell key={column.key} onClick={() => handleSort(column.key)} style={{ cursor: "pointer" }}>
                //   {column.label}
                // </TableCell>
                <TableCell
                  key={column.key}
                  onClick={column.key === "apiName" ? () => handleSort(column.key) : undefined}
                  sx={column.key === "apiName" ? { cursor: "pointer", fontWeight: "bold" } : {}}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataTable!.map((row: any, idx: any) => (
              <TableRow key={idx}>
                {headersTable.map((column: any) => (
                  <TableCell key={column.key} align="left">
                    {column.key === "detail" ? (
                      row[column.key] ? (
                        ""
                      ) : (
                        <Button variant="text" color="primary" size="small" onClick={() => handleDetailClick(row)}>
                          View
                        </Button>
                      )
                    ) : column.key === "status" && row[column.key] !== "MIGRATED" ? (
                      // <Switch checked={row[column.key]} disabled />
                      <Box sx={{ display: "flex" }}>
                        <Warning
                          sx={{
                            color: "#FFC000",
                            mr: 0.5,
                          }}
                          fontSize="small"
                        />
                        <Typography variant="body2">
                          {row[column.key] === "MIGRATED" ? "Migrated" : "Review required"}
                        </Typography>
                      </Box>
                    ) : column.key === "status" && row[column.key] === "MIGRATED" ? (
                      <Box sx={{ display: "flex" }}>
                        <CheckCircle
                          sx={{
                            color: row[column.key] === "MIGRATED" ? "#478B1A" : "#FFC000",
                            mr: 0.5,
                          }}
                          fontSize="small"
                        />
                        <Typography variant="body2">
                          {row[column.key] === "MIGRATED" ? "Migrated" : "Review required"}
                        </Typography>
                      </Box>
                    ) : column.key === "apiSourceFileName" ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleDownloadOriginalFIle(row[column.key])}
                          sx={{
                            textAlign: "left",
                            display: "flex",
                            alignItems: "center",
                            color: "#2064B4",
                            textDecoration: "none",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {row[column.key]}
                          <Download sx={{ ml: 1, fontSize: 16 }} />
                        </Link>
                      </Box>
                    ) : column.key === "apiDestinationFileName" ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleDownloaMigratedFIle(row[column.key])}
                          sx={{
                            textAlign: "left",
                            display: "flex",
                            alignItems: "center",
                            color: "#2064B4",
                            textDecoration: "none",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {row[column.key]}
                          <Download sx={{ ml: 1, fontSize: 16 }} />
                        </Link>
                      </Box>
                    ) : column.key === "complexity" ? (
                      <Chip
                        label={getComplexityLabel(row[column.key])}
                        size="small"
                        sx={{
                          bgcolor: getComplexityBGC(row[column.key]),
                          color: getComplexityColor(row[column.key]),
                          fontSize: "0.75rem",
                          height: "20px",
                        }}
                      ></Chip>
                    ) : column.key === "excluded" ? (
                      <Switch checked={row[column.key]} size="small" disabled={true} />
                    ) : column.key === "candidate" ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {row[column.key] === "Yes" && (
                          <>
                            <CheckCircle sx={{ color: "#4caf50", fontSize: 16, mr: 1 }} />
                            <Typography variant="body2">Yes</Typography>
                          </>
                        )}
                        {row[column.key] === "No" && (
                          <>
                            <Warning sx={{ color: "#ff9800", fontSize: 16, mr: 1 }} />
                            <Typography variant="body2">No</Typography>
                          </>
                        )}
                        {row[column.key] === "Excluded" && (
                          <>
                            <Cancel sx={{ color: "#f44336", fontSize: 16, mr: 1 }} />
                            <Typography variant="body2">Excluded</Typography>
                          </>
                        )}
                      </Box>
                    ) : column.key === "comments" && row[column.key]?.length >= 1 ? (
                      <Button
                        size="small"
                        startIcon={<AIViewIcon sx={{ fontSize: 20 }} />}
                        onClick={() => handleViewComments(row)}
                        sx={{
                          color: "#414141",
                          textTransform: "none",
                          fontWeight: "normal",
                          fontSize: "0.875rem",
                          padding: "2px 8px",
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#2064B4",
                          },
                        }}
                      >
                        View
                      </Button>
                    ) : column.key === "sourceUploadComments" && row[column.key]?.length >= 1 ? (
                      <Button
                        size="small"
                        startIcon={<AIViewIcon sx={{ fontSize: 20 }} />}
                        onClick={() => handleViewErrors(row)}
                        sx={{
                          color: "#414141",
                          textTransform: "none",
                          fontWeight: "normal",
                          fontSize: "0.875rem",
                          padding: "2px 8px",
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                            color: "#2064B4",
                          },
                        }}
                      >
                        View Apigee
                      </Button>
                    ) : (
                      row[column.key]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* API Details Modal */}
      <ApiDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        api={selectedApi}
        migrationId={migrationId!}
      />
      <Drawer
        anchor="right"
        open={commentsOpen}
        // onClose={handleCloseComments}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 400 },
            p: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h6">
            Comments for <strong>{selectedApiName}</strong>
          </Typography>
          <IconButton onClick={handleCloseComments}>
            <Close />
          </IconButton>
        </Box>

        {/* Migration Comments */}
        {selectedComments.length > 0 ? (
          <List>
            {selectedComments.map((comment, index) => (
              <ListItem key={index} sx={{ flexDirection: "column", alignItems: "flex-start", mb: 2 }}>
                <ListItemText
                  primary={`Comment ${index + 1}`}
                  primaryTypographyProps={{ fontWeight: "bold" }}
                  secondary={<Box sx={{ mt: 1 }} dangerouslySetInnerHTML={{ __html: comment }} />}
                />
                {index < selectedComments.length - 1 && <Divider sx={{ width: "100%", mt: 2 }} />}
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              No migration comments available for this API.
            </Typography>
          </Box>
        )}
      </Drawer>

      {/* Api detail modal comments Apigee */}
      <Drawer
        anchor="right"
        open={errorsOpen}
        onClose={handleCloseErrors}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 450 },
            p: 2,
            bgcolor: "#FFFDF7",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 2,
            borderBottom: 1,
            borderColor: "warning.main",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", maxWidth: "calc(100% - 40px)" }}>
            <Warning sx={{ color: "#F57C00", mr: 1, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{
                color: "warning.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Upload Status for {selectedApiName}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseErrors} sx={{ flexShrink: 0 }}>
            <Close />
          </IconButton>
        </Box>

        {/* Source Upload Comments */}
        {selectedSourceUploadComments && selectedSourceUploadComments.length > 0 ? (
          <List sx={{ p: 0 }}>
            {selectedSourceUploadComments.map((comment, index) => (
              <ListItem
                key={index}
                sx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  mb: 2,
                  bgcolor: "#FFF8E1",
                  borderRadius: 1,
                  p: 2,
                  border: "1px solid",
                  borderColor: "warning.light",
                  overflowWrap: "break-word",
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                  hyphens: "auto",
                }}
              >
                <Box sx={{ width: "100%", mb: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "medium",
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      wordBreak: "break-word",
                      hyphens: "auto",
                    }}
                  >
                    {comment.message}
                  </Typography>
                </Box>

                <Box sx={{ width: "100%", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "#F57C00" }}>
                    Code: {comment.code} | Status: {comment.status}
                  </Typography>
                </Box>

                {comment.details && comment.details.length > 0 && (
                  <Box sx={{ width: "100%" }}>
                    <Typography variant="body2" sx={{ fontWeight: "medium", mb: 0.5 }}>
                      Details:
                    </Typography>
                    <List dense sx={{ pl: 2 }}>
                      {comment.details.map((detail: SourceUploadCommentDetail, detailIndex) => (
                        <ListItem
                          key={detailIndex}
                          sx={{
                            display: "list-item",
                            listStyleType: "disc",
                            p: 0,
                            pl: 1,
                            mb: 0.5,
                            overflowWrap: "break-word",
                            wordWrap: "break-word",
                            wordBreak: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflowWrap: "break-word",
                              wordWrap: "break-word",
                              wordBreak: "break-word",
                              hyphens: "auto",
                            }}
                          >
                            {detail["@type"]}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {index < selectedSourceUploadComments.length - 1 && <Divider sx={{ width: "100%", mt: 2 }} />}
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: "center", bgcolor: "#FFF8E1", borderRadius: 1 }}>
            <Typography variant="body1" color="#F57C00">
              No upload status comments available for this API.
            </Typography>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
export default CustomTable;
